import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= across all internal
// navigation so admin's read-only workspace context never silently
// drops back to admin mode mid-walkthrough.
import useShadowNavigate from "@/lib/useShadowNavigate";
// FP3-035F Issue 2 — Detect read-only shadow mode so we can disable
// mutation-creating quick actions (Start Session, Quick Session,
// Draft Goal, Provider Unavailable, Log Session, etc.).
import { useReadOnlyAsProvider } from "@/components/layout/ReadOnlyProviderBanner";
import PageShell from "@/components/layout/PageShell";
import { Play, ChartLineUp, Notepad, Target, ClipboardText, Clock, Warning, TrendDown, CalendarBlank, ArrowRight, Users, Lightning, Gauge, CaretLeft, CaretRight, CaretDown } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
// FP3-124 §21 — Direct date-jump on Today so providers can leap to
// any specific date without arrow-clicking day-by-day. Same shadcn
// Calendar + Popover primitives used elsewhere in the app.
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SessionStatusChip from "@/components/SessionStatusChip";
import { FidelityChip, FIDELITY_META } from "@/components/fidelity/FidelityVisuals";
import { UnavailableBadge, unavailMeta } from "@/components/schedule/UnavailableBadge";
import LogSessionDialog from "@/components/log/LogSessionDialog";
import QuickSessionChoiceDialog from "@/components/log/QuickSessionChoiceDialog";
import DocumentQuickSessionDialog from "@/components/log/DocumentQuickSessionDialog";
// FP3-051 follow-up — Solo provider onboarding checklist.
import SoloSetupChecklist from "@/components/dashboard/SoloSetupChecklist";
// FP3-125B — Read-only diagnostic drawer opened from each Needs
// Attention row so providers can see EXACTLY why a goal is flagged
// stale — rows considered, rows excluded (with reason), and any
// evidence tied to inactive/replaced goals that Data tab still
// shows but freshness can't count.
import WhyStaleDrawer from "@/components/today/WhyStaleDrawer";

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function daysFromNow(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}


export default function TodayDashboard() {
  const { user, provider: authProvider, preferences } = useAuth();
  // FP3-075 — Today's bottom "Minutes fidelity" strip is suppressed
  // when the signed-in user has Fidelity Monitoring turned off so
  // the day no longer leads with a stress signal. The data is still
  // computed server-side; we just skip rendering it.
  const isFidelityOff = preferences?.fidelity_monitoring === "off";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fidelity, setFidelity] = useState(null);
  const [logSessionTarget, setLogSessionTarget] = useState(null);
  const [quickChoiceOpen, setQuickChoiceOpen] = useState(false);
  const [docQuickOpen, setDocQuickOpen] = useState(false);
  // FP3-125B — Diagnostic drawer opened from any Needs Attention row.
  const [whyStaleTarget, setWhyStaleTarget] = useState(null);
  const navigate = useShadowNavigate();
  const readOnly = useReadOnlyAsProvider();

  const localToday = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  // FP3-034E — Date navigation for the Today dashboard. Providers
  // can walk forward/back day-by-day and snap back to "today" without
  // leaving this page. The Today's Sessions card always shows the
  // sessions for `selectedDate`; the fidelity panel scopes to that
  // date as well so an unavailable day reads consistently.
  const [selectedDate, setSelectedDate] = useState(localToday);
  const stepDate = (offsetDays) => {
    const d = new Date(selectedDate + "T12:00:00");
    d.setDate(d.getDate() + offsetDays);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setSelectedDate(next);
  };
  const isToday = selectedDate === localToday;
  const isTomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const t = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return selectedDate === t;
  })();

  const refresh = () => {
    // FP3-028C — Scope Today to the logged-in user's linked provider
    // profile. Without this `/api/today` falls back to the legacy
    // `is_current=True` row (Sarah by seed design), surfacing Sarah's
    // sessions + Sarah's name in the greeting regardless of who's
    // signed in. Admin / no-linked-provider users still call
    // /today unscoped so the backend's `is_current` fallback paints a
    // coherent view.
    setLoading(true);
    const params = { date: selectedDate };
    if (authProvider?.id) params.provider_id = authProvider.id;
    api
      .getTodayDashboard(params)
      .then((r) => {
        setData(r.data);
        const providerId = r.data?.provider?.id;
        if (providerId) {
          api
            .getProviderFidelity({ provider_id: providerId, as_of: selectedDate })
            .then((fr) => setFidelity(fr.data))
            .catch(() => setFidelity(null));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authProvider?.id, selectedDate]);

  // Iter 45p+ — calm reminder for sessions ready to log. Only counts
  // sessions whose scheduled window is already in the past (or running
  // now is excluded — providers should use Live Data Collection for
  // those). Excludes anything already documented or otherwise not in
  // a loggable state. Calm wording, never "overdue" / "missing".
  const sessionsListRef = useRef(null);
  const [highlightId, setHighlightId] = useState(null);
  const readyToLog = useMemo(() => {
    const sessions = data?.today_sessions || [];
    const now = new Date();
    const nowHHMM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const EXCLUDED_STATUSES = new Set([
      "completed",
      "in-progress",
      "in_progress",
      "cancelled",
      "canceled",
      "suppressed",
      "provider_unavailable",
    ]);
    return sessions.filter((s) => {
      const status = (s.status || "").toLowerCase();
      if (EXCLUDED_STATUSES.has(status)) return false;
      // FP3-034E — Sessions in a provider unavailability window can
      // never be logged (provider was OOO), so exclude them from
      // "ready to log" too.
      if (s.provider_unavailable) return false;
      // End time must already be in the past — not currently running, not future.
      const endTime = s.end_time || "";
      if (!endTime || endTime > nowHHMM) return false;
      return true;
    });
  }, [data]);
  const readyToLogIds = useMemo(() => new Set(readyToLog.map((s) => s.id)), [readyToLog]);

  const handleReadyPillClick = () => {
    if (readyToLog.length === 0) return;
    const firstId = readyToLog[0].id;
    setHighlightId(firstId);
    const node = document.querySelector(`[data-testid="session-card-${firstId}"]`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (sessionsListRef.current) {
      sessionsListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Auto-clear highlight after a moment so the page doesn't stay loud.
    setTimeout(() => setHighlightId(null), 2400);
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  // FP3-034E — Selected-date heading. Reads naturally for today /
  // tomorrow, and shows the full weekday+date for any other day.
  const selectedDateFormatted = (() => {
    const d = new Date(selectedDate + "T12:00:00");
    const pretty = d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
    if (isToday) return `Today · ${pretty}`;
    if (isTomorrow) return `Tomorrow · ${pretty}`;
    return pretty;
  })();

  if (loading) {
    return (
      <PageShell testId="today-loading" className="space-y-6">
        <Skeleton className="h-12 w-80" />
        <div className="grid grid-cols-5 gap-3"><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
        <div className="grid grid-cols-3 gap-6"><Skeleton className="h-64" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
      </PageShell>
    );
  }

  const quickActions = [
    { label: "Start Session", icon: Play, onClick: () => navigate("/session"), color: "bg-[#1E3F33] text-white hover:bg-[#173127]" },
    { label: "Quick Session", icon: Lightning, onClick: () => setQuickChoiceOpen(true), color: "bg-[#E8F0EB] text-[#1E3F33] hover:bg-[#d5e5d9]" },
    { label: "Draft Goal", icon: Target, onClick: () => navigate("/goals/new"), color: "bg-[#E8F0EB] text-[#1E3F33] hover:bg-[#d5e5d9]" },
    // Iter 33d Follow-up 14l — Mark Unavailable launches the provider
    // unavailability workflow on the Notifications page where the full
    // form/preview/send + history live. Today stays uncluttered.
    { label: "Provider Unavailable", icon: CalendarBlank, onClick: () => navigate("/notifications?tab=unavailable"), color: "bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#F7EDDF]" },
    { label: "Assessment", icon: ClipboardText, onClick: () => {}, color: "bg-[#F3F1EC] text-[#6B7280] hover:bg-[#e8e5de]" },
  ];

  // FP3-035F Issue 2 — Every Today quick action mutates data (starts
  // a session, drafts a goal, marks unavailable, etc.). In read-only
  // shadow mode the entire row is hidden so admins can't accidentally
  // create records under a provider they don't own.
  const showQuickActions = !readOnly.active;

  return (
    <PageShell testId="today-dashboard">
      {/* Header */}
      <div className="mb-8">
        {/* FP3-028C — Greeting reads the authenticated user's name
            first (matches the sidebar identity surface), then the
            linked provider's first name as a fallback, then the
            /today payload's provider record (admin / no-auth flows).
            Never silently greets a logged-in user with someone
            else's name. */}
        {/* FP3-044B Issue 3 — In read-only shadow mode the greeting
            "Good morning, <admin>" was confusing because the admin is
            viewing another provider's workspace. We swap the greeting
            for a provider-context heading and suppress the personal
            greeting entirely. The detailed admin/provider/role/school
            context lives in the existing read-only banner above. */}
        {readOnly.active ? (
          <h1
            className="text-3xl font-semibold text-[#1F2937] heading-font tracking-tight"
            data-testid="today-greeting"
          >
            {data?.provider?.first_name
              ? `${data.provider.first_name} ${data.provider.last_name || ""}'s Workspace`
              : "Provider Workspace"}
          </h1>
        ) : (
          <h1 className="text-3xl font-semibold text-[#1F2937] heading-font tracking-tight" data-testid="today-greeting">
            {greeting()},{" "}
            {user?.first_name ||
              authProvider?.first_name ||
              data?.provider?.first_name ||
              ""}
          </h1>
        )}
        {/* When admin's logged-in identity differs from the provider
            workspace they're acting against, surface the relationship
            explicitly so the greeting is never misleading. */}
        {!readOnly.active &&
          user &&
          data?.provider?.id &&
          authProvider?.id !== data.provider.id && (
            <p
              data-testid="today-viewing-as"
              className="text-[11px] uppercase tracking-[0.14em] text-[#8FA998] mt-1.5 font-semibold"
            >
              Viewing as {data.provider.first_name} {data.provider.last_name}
            </p>
          )}
        {/* FP3-034E — Date navigator. Prev/Next arrows + "Today" snap
            so providers can review past sessions or look ahead without
            leaving Today. Heading reads "Today · …", "Tomorrow · …",
            or the full weekday for any other date. */}
        <div className="mt-1 flex items-center gap-2" data-testid="today-date-nav">
          <button
            type="button"
            data-testid="today-prev-day"
            onClick={() => stepDate(-1)}
            className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
            title="Previous day"
            aria-label="Previous day"
          >
            <CaretLeft size={14} className="text-[#6B7280]" />
          </button>
          {/* FP3-124 §1 — Direct date jump. Clicking the date label
              opens a compact shadcn Calendar so providers can leap
              to any target date without pressing the ‹ / › arrows
              day-by-day. Arrows + Today snap remain untouched. */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="today-date-picker-trigger"
                className="inline-flex items-center gap-1 rounded-lg border border-[#E5E3DB] px-2 py-1 text-sm text-[#1F2937] tabular-nums hover:bg-[#F3F1EC] hover:border-[#9B9890] transition-colors"
                title="Jump to a specific date"
              >
                <span data-testid="today-selected-date">
                  {selectedDateFormatted}
                </span>
                <CaretDown size={11} className="text-[#6B7280]" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0" data-testid="today-date-picker-popover">
              <Calendar
                data-testid="today-date-picker-calendar"
                mode="single"
                // Parse as local noon anchor so a date-only string
                // like "2026-08-10" never drifts across timezones.
                selected={new Date(selectedDate + "T12:00:00")}
                onSelect={(d) => {
                  if (!d) return;
                  const y = d.getFullYear();
                  const mo = String(d.getMonth() + 1).padStart(2, "0");
                  const dd = String(d.getDate()).padStart(2, "0");
                  setSelectedDate(`${y}-${mo}-${dd}`);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <button
            type="button"
            data-testid="today-next-day"
            onClick={() => stepDate(1)}
            className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
            title="Next day"
            aria-label="Next day"
          >
            <CaretRight size={14} className="text-[#6B7280]" />
          </button>
          {!isToday && (
            <button
              type="button"
              data-testid="today-jump-today"
              onClick={() => setSelectedDate(localToday)}
              className="ml-1 px-3 py-1 text-xs font-medium text-[#1E3F33] bg-[#E8F0EB] rounded-lg hover:bg-[#d5e5d9]"
            >
              Today
            </button>
          )}
        </div>
      </div>

      {/* FP3-051 follow-up — Solo provider workspace setup checklist.
          Self-hides for non-solo callers, when all steps are done,
          or when the user dismisses it. */}
      <SoloSetupChecklist />

      {/* Quick Actions */}
      {showQuickActions && (
        <div className="flex gap-3 mb-8 animate-fade-in-delay-1" data-testid="quick-actions">
          {quickActions.map(({ label, icon: Icon, onClick, color }) => (
            <button
              key={label}
              data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={onClick}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${color}`}
            >
              <Icon size={18} weight="duotone" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Iter 45 — Fidelity alerts moved to bottom (Iter 45o) so the
          Today page doesn't lead with a stress signal. Section is
          rendered after the main grid below; see {fidelityBlock}. */}
      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions Column */}
        <div className="lg:col-span-2 space-y-6 animate-fade-in-delay-2">
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" ref={sessionsListRef}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#1F2937] heading-font flex items-center gap-2 flex-wrap">
                <Clock size={20} weight="duotone" className="text-[#1E3F33]" />
                {isToday ? "Today's Sessions" : isTomorrow ? "Tomorrow's Sessions" : "Sessions"}
                <Badge className="ml-2 bg-[#E8F0EB] text-[#1E3F33] hover:bg-[#E8F0EB]">{data?.today_sessions?.length || 0}</Badge>
                {readyToLog.length > 0 && (
                  <button
                    type="button"
                    data-testid="ready-to-log-pill"
                    onClick={handleReadyPillClick}
                    title="Jump to the next session ready to log"
                    className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#FDF8F3] text-[#9C6B2E] border border-[#9C6B2E]/20 hover:bg-[#F7EDDF] transition-colors"
                  >
                    <Notepad size={12} weight="duotone" />
                    {readyToLog.length} session{readyToLog.length !== 1 ? "s" : ""} ready to log
                  </button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {data?.today_sessions?.length === 0 && (
                <p className="text-sm text-[#6B7280] py-4 text-center">
                  {isToday ? "No sessions scheduled for today" : `No sessions scheduled for ${selectedDateFormatted.replace(/^.*?·\s*/, "")}`}
                </p>
              )}
              {data?.today_sessions?.map((session) => {
                // Show full first names with last initial for grouped sessions until count
                // gets large; only collapse beyond 5 students.
                const studentLabel = (() => {
                  const all = session.students || [];
                  if (all.length === 0) return session.service_type || "Open";
                  const fmt = (s) => `${s.first_name}${s.last_name ? " " + s.last_name[0] + "." : ""}`;
                  if (all.length <= 5) return all.map(fmt).join(", ");
                  return `${all.slice(0, 4).map(fmt).join(", ")} +${all.length - 4} more`;
                })();
                const launchTarget =
                  session.session_ids && session.session_ids.length > 0
                    ? `/session?launch=${session.session_ids.join(",")}`
                    : "/session";
                // Iter 33d Follow-up 11 — Once a session is completed
                // we don't want clicking it to launch a fresh live
                // session (creates duplicate data). The card stays
                // clickable to view the first underlying student's
                // history, but the live-session navigation is gated.
                const isCompleted = (session.status || "").toLowerCase() === "completed";
                // FP3-034E — Provider unavailable annotation. Mute and
                // surface the treatment chip; also disable Log Session
                // (the provider was OOO so there's nothing to log).
                const unavail = unavailMeta(session);
                const firstStudentId = session.students?.[0]?.id;
                const onCardClick = () => {
                  if (unavail) {
                    if (firstStudentId) navigate(`/students/${firstStudentId}`);
                    return;
                  }
                  if (isCompleted) {
                    if (firstStudentId) {
                      navigate(`/students/${firstStudentId}?tab=sessions`);
                    }
                    return;
                  }
                  // FP3-035F Issue 2 — Launching Live Session would
                  // create live data under the shadowed provider; in
                  // read-only mode, send the admin to the student
                  // workspace overview instead.
                  if (readOnly.active) {
                    if (firstStudentId) navigate(`/students/${firstStudentId}`);
                    return;
                  }
                  navigate(launchTarget);
                };
                return (
                  <div
                    key={session.id}
                    data-testid={`session-card-${session.id}`}
                    data-session-status={session.status}
                    data-provider-unavailable={unavail ? "true" : "false"}
                    data-unavail-treatment={unavail?.treatment || ""}
                    data-ready-to-log={readyToLogIds.has(session.id) ? "true" : "false"}
                    className={`flex items-center gap-4 p-3 rounded-xl transition-all group ${
                      unavail
                        ? `${unavail.palette.cardBg} border ${unavail.palette.cardBorder} ${unavail.palette.cardOpacity} cursor-pointer hover:shadow-sm`
                        : isCompleted
                        ? "bg-[#F9F8F6]/60 cursor-default"
                        : "hover:bg-[#F9F8F6] cursor-pointer"
                    } ${
                      highlightId === session.id
                        ? "ring-2 ring-[#9C6B2E]/40 bg-[#FDF8F3]"
                        : ""
                    }`}
                    onClick={onCardClick}
                    title={
                      unavail
                        ? `Provider ${unavail.reasonLabel.toLowerCase()} — ${unavail.chipLabel}`
                        : isCompleted
                        ? "Session already completed — open Session History on the student page to edit"
                        : undefined
                    }
                  >
                    <div className="text-right min-w-[80px]">
                      <p className="text-sm font-medium text-[#1F2937]">{formatTime(session.start_time)}</p>
                      <p className="text-xs text-[#6B7280]">{formatTime(session.end_time)}</p>
                    </div>
                    <Separator orientation="vertical" className="h-10 bg-[#E5E3DB]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1F2937] truncate">{studentLabel}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {unavail && (
                          <UnavailableBadge session={session} size="sm" />
                        )}
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 ${
                            session.is_grouped
                              ? "border-[#1E3F33]/30 bg-[#E8F0EB] text-[#1E3F33]"
                              : "border-[#E5E3DB] text-[#6B7280]"
                          }`}
                          data-testid={session.is_grouped ? "session-group-badge" : "session-type-badge"}
                        >
                          {session.is_grouped && <Users size={10} weight="duotone" className="mr-0.5 inline" />}
                          {session.is_grouped ? `group · ${session.students.length}` : session.session_type}
                        </Badge>
                        {session.service_type && (
                          <span className="text-[10px] text-[#8FA998]">{session.service_type}</span>
                        )}
                        {/* Iter 32 — session completion status. Surfaces
                            'Completed' (session has ended + data recorded),
                            'In progress' (running right now), or
                            'Missed'/'Cancelled' once those states land.
                            FP3-122 — Reads `session_outcome` too so a
                            Held→Provider-Unavailable correction shows
                            the amber pill on Today just like Session
                            History and Provider Calendar. */}
                        <SessionStatusChip
                          status={session.status}
                          attendance={session.attendance}
                          sessionOutcome={session.session_outcome}
                        />
                      </div>
                    </div>
                    {!isCompleted && !unavail && !readOnly.active && (
                      <button
                        type="button"
                        data-testid={`log-session-btn-${session.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogSessionTarget(session);
                        }}
                        className="text-[11px] font-medium text-[#1E3F33] border border-[#1E3F33]/30 rounded-full px-2.5 py-1 hover:bg-[#E8F0EB] transition-colors flex items-center gap-1"
                        title="Log this session after the fact"
                      >
                        <ClipboardText size={12} weight="duotone" />
                        Log Session
                      </button>
                    )}
                    <ArrowRight size={16} className={`text-[#6B7280] transition-opacity ${isCompleted || unavail ? "opacity-0" : "opacity-0 group-hover:opacity-100"}`} />
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Students Needing Attention */}
          {data?.attention_needed?.length > 0 && (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-[#1F2937] heading-font flex items-center gap-2">
                  <Warning size={20} weight="duotone" className="text-[#D9A05B]" />
                  Needs Attention
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.attention_needed.map((item, i) => (
                  <div
                    key={i}
                    data-testid={`attention-item-${i}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#FDF8F3] transition-colors"
                  >
                    <div
                      className="w-8 h-8 rounded-full bg-[#FDF8F3] flex items-center justify-center cursor-pointer"
                      onClick={() => navigate(`/students/${item.student?.id}`)}
                    >
                      <TrendDown size={16} weight="duotone" className="text-[#D9A05B]" />
                    </div>
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/students/${item.student?.id}`)}
                    >
                      <p className="text-sm font-medium text-[#1F2937]">
                        {item.student?.first_name} {item.student?.last_name}
                      </p>
                      <p className="text-xs text-[#6B7280]" data-testid={`attention-summary-${i}`}>
                        {item.declining_goals > 0 && `${item.declining_goals} declining`}
                        {item.declining_goals > 0 && item.plateauing_goals > 0 && ", "}
                        {item.plateauing_goals > 0 && `${item.plateauing_goals} plateauing`}
                        {(item.declining_goals > 0 || item.plateauing_goals > 0) && item.student_stale && ", "}
                        {/* FP3-125C — Student-level stale copy. Prior
                            wording ("N stale (no data 3+ wk)")
                            described goal-level counts as if the whole
                            student was stale, which was clinically
                            wrong when the student had current data on
                            OTHER goals. Now: student is only labelled
                            stale when the freshest active goal is past
                            the 3-week cutoff. */}
                        {item.student_stale && (
                          <span className="text-[#9C6B2E]" data-testid={`attention-stale-${i}`}>
                            {item.student_last_data_date
                              ? `No data since ${item.student_last_data_date}`
                              : "No data recorded"}
                          </span>
                        )}
                      </p>
                    </div>
                    {/* FP3-125B — Read-only "Why stale?" diagnostic.
                        Shown whenever this student has goal-level
                        stale metadata (>=1 goal not targeted recently),
                        even if the student's OVERALL data is fresh
                        (FP3-125C). Lets providers see which specific
                        goals need targeting next session. */}
                    {item.stale_goals > 0 && item.student?.id && (
                      <button
                        type="button"
                        data-testid={`why-stale-btn-${i}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setWhyStaleTarget({
                            studentId: item.student.id,
                            studentName: `${item.student.first_name || ""} ${item.student.last_name || ""}`.trim(),
                          });
                        }}
                        className="text-[11px] px-2 py-1 rounded-full bg-white text-[#9C6B2E] border border-[#E9DAC0] hover:bg-[#FDF8F3] transition-colors whitespace-nowrap"
                        title={
                          item.student_stale
                            ? "See exactly why this student's data is flagged stale"
                            : `${item.stale_goals} goal${item.stale_goals === 1 ? "" : "s"} not targeted recently — see which`
                        }
                      >
                        Why stale?
                      </button>
                    )}
                    <ArrowRight
                      size={14}
                      className="text-[#6B7280] cursor-pointer"
                      onClick={() => navigate(`/students/${item.student?.id}`)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6 animate-fade-in-delay-3">
          {/* Stats */}
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
            <CardContent className="p-5">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-semibold text-[#1E3F33] heading-font">{data?.stats?.sessions_today || 0}</p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">Sessions</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#1E3F33] heading-font">{data?.stats?.total_students || 0}</p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">Students</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#1E3F33] heading-font">{data?.stats?.goals_active || 0}</p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">Active Goals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Due Soon */}
          {(data?.due_soon?.length > 0 || data?.overdue?.length > 0) && (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
              <CardHeader className="pb-3">
                {/* FP3-060 — Title is now a deep-link into the Due
                    Dates hub filtered to "This week". */}
                <CardTitle
                  data-testid="today-due-this-week-link"
                  onClick={() => navigate("/due-dates?range=this_week")}
                  className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2 cursor-pointer hover:text-[#1E3F33] group"
                >
                  <CalendarBlank size={18} weight="duotone" className="text-[#D9A05B]" />
                  Due This Week
                  <ArrowRight
                    size={13}
                    className="text-[#9AA0A6] group-hover:text-[#1E3F33] opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.overdue?.map((item, i) => (
                  <div
                    key={`o-${i}`}
                    data-testid={`overdue-item-${i}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-[#FDF2F2] cursor-pointer"
                    onClick={() => navigate(`/students/${item.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#B85C5C] truncate">{item.first_name} {item.last_name}</p>
                      <p className="text-xs text-[#B85C5C]/70">{item.due_type} overdue</p>
                    </div>
                  </div>
                ))}
                {data.due_soon?.map((item, i) => (
                  <div
                    key={`d-${i}`}
                    data-testid={`due-soon-item-${i}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#FDF8F3] transition-colors cursor-pointer"
                    onClick={() => navigate(`/students/${item.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1F2937] truncate">{item.first_name} {item.last_name}</p>
                      <p className="text-xs text-[#D9A05B]">{item.due_type} in {daysFromNow(item.due_date)} days</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Upcoming IEP/MET */}
          {data?.upcoming_iep_met?.length > 0 && (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
                  <CalendarBlank size={18} weight="duotone" className="text-[#4A6B8C]" />
                  Upcoming IEP / MET
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {data.upcoming_iep_met.slice(0, 6).map((item, i) => (
                  <div
                    key={i}
                    data-testid={`upcoming-iep-${i}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-[#F3F1EC] transition-colors cursor-pointer"
                    onClick={() => navigate(`/students/${item.student_id}`)}
                  >
                    <div>
                      <p className="text-sm text-[#1F2937]">{item.student_name}</p>
                      <p className="text-xs text-[#6B7280]">{item.type}</p>
                    </div>
                    <span className="text-xs text-[#4A6B8C] font-medium">{daysFromNow(item.date)}d</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Iter 45o — Minutes Fidelity moved to the bottom of Today so it
          doesn't lead the day with a stress signal. Surfaces only
          behind/met/over; "on_track" intentionally suppressed per
          product spec — Today should only show what changes provider
          behavior. Same logic, same provider scoping, same data — just
          relocated for emotional calm. */}
      {fidelity && !isFidelityOff && (() => {
        const todaysIds = new Set(
          (data?.today_sessions || []).flatMap((s) => (s.students || []).map((x) => x.id))
        );
        const relevant = (fidelity.students || []).filter(
          (s) =>
            todaysIds.has(s.student_id) &&
            // FP3-034D — Surface the new excusal-aware statuses
            // alongside the legacy ones so providers see "Met
            // after exclusions" and "Needs make-up" in the same
            // place as Met / Behind / Over.
            ["behind", "met", "over", "met_after_exclusions", "needs_makeup"].includes(
              s.status_rollup
            )
        );
        if (relevant.length === 0) return null;
        return (
          <div className="mt-8 pt-6 border-t border-[#E5E3DB] space-y-2" data-testid="today-fidelity-alerts">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold flex items-center gap-1.5">
              <Gauge size={12} weight="duotone" /> Minutes fidelity — today&apos;s students
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {relevant.map((s) => {
                const primary = s.commitments[0];
                const meta = FIDELITY_META[s.status_rollup] || FIDELITY_META.on_track;
                const toneBg = meta.tone === "red" ? "bg-[#FBEFEC]/60 border-[#B85C5C]/25"
                  : meta.tone === "amber" ? "bg-[#FDF8F3] border-[#9C6B2E]/20"
                  : "bg-[#E8F0EB]/60 border-[#1E5A3F]/20";
                const toneText = meta.tone === "red" ? "text-[#B85C5C]"
                  : meta.tone === "amber" ? "text-[#9C6B2E]" : "text-[#1E5A3F]";
                return (
                  <button
                    key={s.student_id}
                    data-testid={`today-fidelity-${s.student_id}`}
                    onClick={() => navigate(`/students/${s.student_id}?tab=overview`)}
                    className={`text-left px-3 py-2 rounded-lg border transition-shadow hover:shadow-sm ${toneBg}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12px] font-medium text-[#1F2937] truncate">
                        {s.first_name} {s.last_name}
                      </span>
                      <FidelityChip status={s.status_rollup} size="xs" />
                    </div>
                    {primary && (
                      <p className={`text-[10.5px] ${toneText} mt-0.5 tabular-nums`}>
                        {primary.delivered_minutes}/{primary.target_minutes} min this {primary.cadence}
                        <span className="text-[#9B9890] ml-1">· {primary.label}</span>
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}
      <LogSessionDialog
        open={!!logSessionTarget}
        onOpenChange={(o) => !o && setLogSessionTarget(null)}
        session={logSessionTarget}
        onLogged={() => {
          setLogSessionTarget(null);
          refresh();
        }}
      />
      {/* FP3-125B — Diagnostic drawer. Stays mounted; renders only
          when `whyStaleTarget` is set. Refresh Today after close so
          any subsequent data-writes surface immediately. */}
      <WhyStaleDrawer
        studentId={whyStaleTarget?.studentId}
        studentName={whyStaleTarget?.studentName}
        open={!!whyStaleTarget}
        onOpenChange={(o) => !o && setWhyStaleTarget(null)}
      />
      <QuickSessionChoiceDialog
        open={quickChoiceOpen}
        onOpenChange={setQuickChoiceOpen}
        onStartLive={() => navigate("/session?quick=true")}
        onDocument={() => setDocQuickOpen(true)}
      />
      <DocumentQuickSessionDialog
        open={docQuickOpen}
        onOpenChange={setDocQuickOpen}
        provider={data?.provider}
        onSaved={() => {
          setDocQuickOpen(false);
          refresh();
        }}
      />
    </PageShell>
  );
}
